import { DoseSpotPharmaciesResponse, DoseSpotPharmacy, MODULE_TYPE } from '@abiglobalhealth/go-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getPharmacies, setFulfillmentCollect } from '@/api/pharmacy'
import { BackgroundPattern } from '@/components/BackgroundPattern'
import { Button } from '@/components/Button'
import { Close } from '@/components/icons/Close'
import { FilterIcon } from '@/components/icons/FilterIcon'
import { NotFound } from '@/components/icons/NotFound'
import { Select } from '@/components/Select'
import { UserFormData } from '@/contexts/userForm'
import { useButtonClick } from '@/hooks/useButtonClick'
import { useConsultation } from '@/hooks/useConsultation'
import useDebounce from '@/hooks/useDebounce'
import { useModule } from '@/hooks/useModule'
import { usePharmacy } from '@/hooks/usePharmacy'
import { useUser } from '@/hooks/useUser'
import { US_STATES } from '@/utils/us-states'

import { Filters } from './Filters'
import { PharmacyCard } from './PharmacyCard'
import { PharmacyCardSkeleton } from './PharmacyCard/skeleton'
import { FiltersT } from './schema'

const DOSE_SPOT_STATES = [US_STATES.CA, US_STATES.FL, US_STATES.TX]

const getStateKey = (state: US_STATES) => `country.us.area.${state}`

export const DoseSpotPharmacySelector = () => {
  const { currentModule } = useModule()

  const [pharmacies, setPharmacies] = useState<DoseSpotPharmacy[]>([])
  const { t } = useTranslation()
  const { user } = useUser()
  const { handleButtonClick } = useButtonClick()
  const { selectedPharmacy, updateSelectedPharmacy } = usePharmacy()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const { updateConsultation } = useConsultation()
  const initialState = useMemo(() => {
    const country = user?.data?.country
    if (country?.code.startsWith('us-')) return country?.code.split('-')[1]
    return DOSE_SPOT_STATES[0]
  }, [user])
  const [filters, setFilters] = useState<FiltersT>({
    state: initialState
  })
  const filtersArray = useMemo(() => Object.keys(filters).filter((key) => key !== 'state'), [filters])
  const debouncedFilters = useDebounce(filters, 750)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loadingMore, setLoadingMore] = useState(true)

  const [filtersOpen, setFiltersOpen] = useState(false)

  const addPharmacies = (newPharmacies: DoseSpotPharmacy[]) => {
    setPharmacies((prev) => [...prev, ...newPharmacies])
  }

  const queryPharmacies = async (filters: FiltersT, page: number = 1) => {
    setLoading(true)

    getPharmacies({
      uniqueId: user?.data?.uniqueId!,
      filter: { ...filters, pageNumber: page }
    }).then((res) => {
      const response = res as DoseSpotPharmaciesResponse
      setLoading(false)
      setTotalPages(response.totalPages)
      setCurrentPage(response.page)

      setLoadingMore(false)
      if (page > 1) return addPharmacies(response.pharmacies)

      setPharmacies(response.pharmacies)
    })
  }

  const handlePharmacySelected = (pharmacy: DoseSpotPharmacy) => {
    setError(null)
    updateSelectedPharmacy(pharmacy)
  }

  const handleContinue = async () => {
    if (!selectedPharmacy) {
      setError('pharmacy_selector.validation')
      return
    }

    if (currentModule?.type === MODULE_TYPE.PHARMACY_SELECTOR) {
      handleButtonClick(currentModule.buttonAction)
      return
    }

    setLoading(true)

    const consultation = await setFulfillmentCollect({
      uniqueId: user?.data?.uniqueId!,
      pharmacyId: String(selectedPharmacy.id)
    })
    updateConsultation(consultation)
    setLoading(false)
  }

  const sortStates = (a: US_STATES, z: US_STATES) => t(getStateKey(a)).localeCompare(t(getStateKey(z)))

  const nextPage = () => {
    setLoadingMore(true)
    queryPharmacies(debouncedFilters, currentPage + 1)
  }

  const updateFilter = (id: keyof FiltersT, value: UserFormData | undefined) => {
    if (value === undefined || filters[id] === value) return

    setLoadingMore(true)
    setPharmacies([])

    setFilters((prev) => {
      const newFilters = { ...prev }
      if (value) {
        newFilters[id] = value as never
      } else {
        delete newFilters[id]
      }
      return newFilters
    })
  }

  const removeFilter = (id: keyof FiltersT) => {
    setLoadingMore(true)
    setPharmacies([])

    setFilters((prev) => {
      const newFilters = { ...prev }
      delete newFilters[id]
      return newFilters
    })
  }

  const clearFilters = () => {
    setLoadingMore(true)
    setPharmacies([])
    setFilters({ state: initialState })
  }

  useEffect(() => {
    queryPharmacies(debouncedFilters)
  }, [debouncedFilters])

  useEffect(() => {
    queryPharmacies({ state: initialState })
  }, [])

  return (
    <>
      <BackgroundPattern className="bg-gray-background flex grow flex-col gap-4 overflow-auto p-4">
        <h2>{t('pharmacy_selector.description')}</h2>

        <div className="flex items-center gap-4">
          <Select
            id="state"
            tabIndex={3}
            className="py-1"
            options={DOSE_SPOT_STATES.sort(sortStates).map((state) => ({
              id: state,
              textKey: getStateKey(state),
              value: state
            }))}
            value={filters.state}
            onChange={(state) => updateFilter('state', state.value)}
            errorHidden
          />

          <button onClick={() => setFiltersOpen(true)} className="h-fit disabled:cursor-not-allowed disabled:opacity-50" disabled={loading}>
            <FilterIcon className="text-primary aspect-square w-7" />
          </button>
        </div>

        {filtersArray.length > 0 && (
          <div className="animate-fade-in flex gap-1 overflow-x-auto pb-1">
            {filtersArray.map((key) => (
              <span key={key} className="flex items-center gap-3 rounded-full bg-white px-4 py-2 whitespace-nowrap">
                {t(`prescriptions-us.pharmacy.filter.${key}.title`)}
                <button onClick={() => removeFilter(key as keyof FiltersT)}>
                  <Close customClassName="w-[14px] aspect-square" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="from-gray-background pointer-events-none absolute right-0 bottom-24 left-0 z-10 h-16 bg-gradient-to-t to-transparent" />

        {pharmacies?.length > 0 || loadingMore ? (
          <div className="flex flex-1 flex-col gap-[3px] overflow-y-auto p-1">
            {pharmacies?.map((pharmacy) => (
              <PharmacyCard
                key={pharmacy.id}
                pharmacy={pharmacy}
                onClick={handlePharmacySelected}
                isSelected={selectedPharmacy?.id === pharmacy.id}
              />
            ))}

            {loadingMore && Array.from({ length: 10 }).map((_, i) => <PharmacyCardSkeleton key={i} />)}

            {totalPages > currentPage && !loadingMore && (
              <button onClick={nextPage} className="text-primary z-10 my-4 font-bold uppercase">
                {t('prescriptions-us.pharmacy.load_more')}
              </button>
            )}
          </div>
        ) : (
          <div className="animate-fade-in flex flex-1 flex-col items-center justify-center gap-5 py-8">
            <NotFound className="text-gray-text aspect-square w-10" />
            <p className="text-gray-text text-center">{t('prescriptions-us.pharmacy.no-pharmacies')}</p>
          </div>
        )}

        {error && (
          <span className="animate-fade-in text-error text-sm" data-testid="error-span">
            {t(error)}
          </span>
        )}

        <Button onClick={handleContinue} loading={loading}>
          {t('button.continue')}
        </Button>
      </BackgroundPattern>

      {filtersOpen && <Filters filters={debouncedFilters} updateFilter={updateFilter} setFiltersOpen={setFiltersOpen} clearFilters={clearFilters} />}
    </>
  )
}
